import type { CSSProperties } from 'react';
import { brandForeground, brandStyleVars } from '@/components/onlineBooking/brandTheme';
import { normalizeHexColor, publicFormFontFamily } from '@/lib/forms/public-settings';
import type {
  FormBranding,
  PublicFormHeaderStyle,
  PublicFormQuestionStyle,
} from '@/types/formsEngine';

export const PUBLIC_FORM_HEADER_STYLES: ReadonlyArray<{
  value: PublicFormHeaderStyle;
  label: string;
  description: string;
}> = [
  { value: 'gradient', label: 'Gradient', description: 'Brand colour fading to a deeper shade' },
  { value: 'solid', label: 'Solid', description: 'One flat brand colour' },
  { value: 'minimal', label: 'Minimal', description: 'White, with a brand strip on top' },
];

export const PUBLIC_FORM_QUESTION_STYLES: ReadonlyArray<{
  value: PublicFormQuestionStyle;
  label: string;
  description: string;
}> = [
  { value: 'cards', label: 'Cards', description: 'Each question on a softly tinted card' },
  { value: 'classic', label: 'Classic', description: 'Questions stacked inside one box' },
];

export { PUBLIC_FORM_BACKGROUNDS } from '@/lib/forms/public-settings';

export const DEFAULT_HEADER_STYLE: PublicFormHeaderStyle = 'gradient';
export const DEFAULT_QUESTION_STYLE: PublicFormQuestionStyle = 'cards';

/** The neutral the brand tint is mixed into, and the page colour with no brand at all. */
const PAGE_NEUTRAL = '#f8fafc';

/**
 * The light theme's surface tokens. A public page is always light; the
 * builder's preview of one sits inside a dashboard that may be dark, so it
 * pins these to show the page as customers will see it.
 */
export const PUBLIC_FORM_LIGHT_TOKENS = {
  colorScheme: 'light',
  color: 'var(--foreground)',
  ['--background' as never]: '#ffffff',
  ['--foreground' as never]: '#0a0b0d',
  ['--card' as never]: '#ffffff',
  ['--card-foreground' as never]: '#0a0b0d',
  ['--primary' as never]: '#0a0b0d',
  ['--primary-foreground' as never]: '#f7f7f8',
  ['--muted' as never]: '#f0f0f2',
  ['--muted-foreground' as never]: '#6b6b70',
  ['--border' as never]: '#e6e6e9',
  ['--input' as never]: '#d7d7dc',
} as CSSProperties;

/**
 * The colour behind the form card. An embed with no colour picked stays
 * white, as embeds always were, so it sits cleanly on the tenant's website.
 */
export function publicFormPageBackground(
  brandColor: string | null | undefined,
  backgroundColor: string | null | undefined,
  embedded = false,
): string {
  const background = normalizeHexColor(backgroundColor);
  if (background) return background;
  if (embedded) return '#ffffff';
  const brand = normalizeHexColor(brandColor);
  return brand ? `color-mix(in srgb, ${brand} 9%, ${PAGE_NEUTRAL})` : PAGE_NEUTRAL;
}

function relativeLuminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  const [r, g, b] = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((channel) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio of two `#rrggbb` colours, 1 (same) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
}

/**
 * True when a coloured header would barely stand out from the page around
 * it. Only a picked background can clash: the default tint is mixed from the
 * brand colour and stays pale.
 */
export function headerClashesWithBackground(
  brandColor: string | null | undefined,
  backgroundColor: string | null | undefined,
): boolean {
  const brand = normalizeHexColor(brandColor);
  const background = normalizeHexColor(backgroundColor);
  if (!brand || !background) return false;
  return contrastRatio(brand, background) < 1.6;
}

/**
 * CSS variables for the form: the brand tokens from `brandStyleVars`, plus
 * the tints the cards, tiles and header use. All derived from strict hex via
 * `color-mix`, so nothing tenant-authored reaches `style` as raw CSS.
 */
export function publicFormThemeStyle(branding: FormBranding | null): CSSProperties {
  const brand = normalizeHexColor(branding?.primaryColor);
  // The header's far end: deeper under light text, lighter under dark text,
  // so the text keeps its contrast across the whole gradient. The default
  // theme's primary is near-black, which takes light text.
  const darkText = brand ? brandForeground(brand) !== '#ffffff' : false;
  return {
    ...brandStyleVars(brand),
    ...(branding ? { fontFamily: publicFormFontFamily(branding.font) } : {}),
    ['--form-tint' as never]: 'color-mix(in srgb, var(--primary) 5%, white)',
    ['--form-tint-strong' as never]: 'color-mix(in srgb, var(--primary) 10%, white)',
    ['--form-tint-border' as never]: 'color-mix(in srgb, var(--primary) 18%, transparent)',
    ['--form-primary-hover' as never]: 'color-mix(in srgb, var(--primary) 85%, black)',
    ['--form-header-end' as never]: darkText
      ? 'color-mix(in srgb, var(--primary) 65%, white)'
      : 'color-mix(in srgb, var(--primary) 70%, black)',
  } as CSSProperties;
}

/** The header's far end for a brand colour, matching `--form-header-end`. */
export function headerEndFor(brandColor: string): string {
  return brandForeground(brandColor) === '#ffffff'
    ? `color-mix(in srgb, ${brandColor} 70%, black)`
    : `color-mix(in srgb, ${brandColor} 65%, white)`;
}

/** The header fill for a style; minimal has none of its own. */
export function publicFormHeaderFill(style: PublicFormHeaderStyle): string | undefined {
  if (style === 'gradient') {
    return 'linear-gradient(135deg, var(--primary) 0%, var(--form-header-end) 100%)';
  }
  if (style === 'solid') return 'var(--primary)';
  return undefined;
}
